import Vue from "vue";
import moment from "moment";
import PulseTrendChart from "@/components/PulseInsights/PulseTrendChart.vue";
import { getPulseTrackTheme, hexToRgba, } from "@/components/PulseInsights/pulseTracks";
// Presentation copy for the three fixed tracks (first-time category cards).
const TRACK_TAGLINES = {
    pulse_well_in_mind: "Mood, stress & mental resilience",
    pulse_well_in_body: "Energy, movement & physical health",
    pulse_well_in_life: "Purpose, balance & relationships",
};
const TRACK_BLURBS = {
    pulse_well_in_mind: "How your emotional and psychological health is affecting your day-to-day life — and where support could help most.",
    pulse_well_in_body: "How your physical habits and health are impacting your energy, performance, and overall sense of wellbeing.",
    pulse_well_in_life: "How connected, fulfilled, and balanced you feel across your work, relationships, and sense of purpose.",
};
export default Vue.extend({
    name: "PulseInsights",
    components: { PulseTrendChart },
    data() {
        return {
            loading: true,
            // which track's line the trend chart is focused on ("" = default to
            // the first active track); set by clicking a score card
            selectedTrackSlug: "",
            // local mirror of the reminder-email opt-in; seeded from get-landing and
            // reconciled against the set-reminders response
            remindersEnabled: false,
            remindersSaving: false,
        };
    },
    computed: {
        landing() {
            return this.$store.getters.pulseLanding || null;
        },
        tracks() {
            return this.landing && Array.isArray(this.landing.tracks)
                ? this.landing.tracks
                : [];
        },
        activeTracks() {
            return this.tracks.filter((track) => track.active === 1);
        },
        // slugs of the tracks the user still has — the only lines the chart draws
        activeTrackSlugs() {
            return this.activeTracks.map((track) => track.track_slug);
        },
        // the track currently in focus — the clicked one, or the first active
        effectiveSelectedSlug() {
            if (this.selectedTrackSlug)
                return this.selectedTrackSlug;
            return this.activeTracks.length ? this.activeTracks[0].track_slug : "";
        },
        // short key (mind | body | life) the trend chart emphasises
        selectedKey() {
            return getPulseTrackTheme(this.effectiveSelectedSlug).key;
        },
        composite() {
            return this.landing ? this.landing.composite || null : null;
        },
        hasCompleted() {
            return !!(this.landing && this.landing.has_completed);
        },
        inProgress() {
            return !!(this.landing && this.landing.in_progress);
        },
        // returning user (has ≥1 completed check-in) → Overview, else Landing
        isOverview() {
            return this.hasCompleted;
        },
        trends() {
            const series = this.$store.getters.pulseTrends;
            return Array.isArray(series) ? series : [];
        },
        contentItems() {
            const report = this.$store.getters.pulseReport;
            const items = report && Array.isArray(report.content_items)
                ? report.content_items
                : [];
            // drop items whose source record is missing (null title); P1 before P2
            return items
                .filter((item) => item.title)
                .sort((a, b) => a.priority > b.priority ? 1 : -1);
        },
        heroCtaLabel() {
            if (this.inProgress)
                return "Resume your check-in →";
            return this.hasCompleted
                ? "Start your next Insights Check-in →"
                : "Start Insights →";
        },
        heroDesc() {
            return this.hasCompleted
                ? "The more you check in, the better we understand what's working — and where you need extra support."
                : "We match recommended services, courses, and content to exactly where you are.";
        },
        lastCompletedLabel() {
            const completedOn = this.composite && this.composite.completed_on;
            return completedOn ? moment(completedOn).format("D MMM YYYY") : "";
        },
        remindersSubtext() {
            return this.remindersEnabled
                ? "On — you will be reminded"
                : "Off — reminders are paused";
        },
    },
    mounted() {
        this.fetchLanding();
    },
    methods: {
        themeFor(slug) {
            return getPulseTrackTheme(slug);
        },
        taglineFor(slug) {
            return TRACK_TAGLINES[slug] || "";
        },
        blurbFor(slug) {
            return TRACK_BLURBS[slug] || "";
        },
        cardStyle(slug) {
            const theme = getPulseTrackTheme(slug);
            return {
                backgroundColor: hexToRgba(theme.color, 0.07),
                borderColor: hexToRgba(theme.color, 0.18),
            };
        },
        // focus the trend chart on this track
        selectTrack(slug) {
            this.selectedTrackSlug = slug;
        },
        // colour the selected score card's border/ring in the track's accent
        selectedCardStyle(slug) {
            const theme = getPulseTrackTheme(slug);
            return {
                borderColor: theme.color,
                boxShadow: `0 0 0 1px ${theme.color}`,
            };
        },
        // idempotent SET (not a flip): send the explicit desired state, then treat
        // the returned reminders_enabled as the source of truth
        async setReminders(enabled) {
            if (this.remindersSaving)
                return;
            const previous = this.remindersEnabled;
            // optimistic flip
            this.remindersEnabled = enabled;
            this.remindersSaving = true;
            const res = await this.api({
                path: "api/u/pulse/set-reminders",
                enabled,
            });
            this.remindersSaving = false;
            if (res &&
                res.success !== false &&
                typeof res.reminders_enabled === "boolean") {
                this.remindersEnabled = res.reminders_enabled;
                // keep the cached landing in step so returning to this screen paints
                // the correct state before the next get-landing refresh. Patch the
                // field in place (not a whole new landing object) so landing-derived
                // computeds don't invalidate and re-render the trend chart.
                this.$store.commit("setPulseRemindersEnabled", res.reminders_enabled);
            }
            else {
                // failed / rejected → roll back to where we started
                this.remindersEnabled = previous;
            }
        },
        startCheckIn() {
            this.$router.push("/personal/insights/check-in").catch(() => { });
        },
        viewReport() {
            this.$router.push("/personal/insights/report").catch(() => { });
        },
        // clicking a point on the trend chart opens the report for that day
        goToReportForDate(date) {
            if (!date)
                return;
            this.$router
                .push({ path: "/personal/insights/report", query: { date } })
                .catch(() => { });
        },
        goToContent(item) {
            if (item.type === "course") {
                this.$router.push(`/wellbeing/${item.id}`).catch(() => { });
            }
            else if (item.type === "stand_alone_article") {
                this.$router.push(`/wellbeing/article/${item.id}`).catch(() => { });
            }
        },
        async toggleFavourite(item) {
            if (item.type !== "course" && item.type !== "stand_alone_article")
                return;
            const previous = !!item.is_favourited;
            // optimistically flip the heart, then reconcile with the server
            this.$set(item, "is_favourited", !previous);
            const res = await this.api({
                path: "api/u/pulse/toggle-favourite",
                type: item.type,
                id: item.id,
            });
            // favourites are shared platform-wide; trust the returned state, and
            // revert the optimistic flip if the call failed
            const confirmed = res && res.success ? !!res.is_favourited : previous;
            this.$set(item, "is_favourited", confirmed);
        },
        async fetchLanding() {
            const cached = this.$store.getters.pulseLanding;
            if (!cached)
                this.loading = true;
            // paint the reminder toggle from cache instantly, then reconcile below
            else
                this.remindersEnabled = !!cached.reminders_enabled;
            const landing = await this.api({
                path: "api/u/pulse/get-landing",
                noop: true,
            });
            if (landing && landing.success !== false) {
                this.$store.commit("setPulseLanding", landing);
                // don't clobber an in-flight optimistic toggle
                if (!this.remindersSaving)
                    this.remindersEnabled = !!landing.reminders_enabled;
            }
            // returning users: pull the trend series + latest report (content recs)
            if (this.hasCompleted) {
                await Promise.all([this.fetchTrends(), this.fetchReport()]);
            }
            this.loading = false;
        },
        async fetchTrends() {
            const res = await this.api({
                path: "api/u/pulse/get-trends",
                noop: true,
            });
            if (res && Array.isArray(res.trends)) {
                this.$store.commit("setPulseTrends", res.trends);
            }
        },
        async fetchReport() {
            const res = await this.api({
                path: "api/u/pulse/get-report",
                noop: true,
            });
            if (res && res.report) {
                this.$store.commit("setPulseReport", res.report);
            }
        },
    },
});
